/**
 * PaymentHistory
 *
 * Read-only view of paid referrals. Mirrors ReferralList structure
 * but fetches from the payment-history endpoint and adds paid_at / payment_tx_id columns.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getRequest } from '../../utils/httpRequest';
import { API_ENDPOINTS, ITEMS_PER_PAGE } from './constants';
import { getStatusBadgeStyle, formatReferralDate, calculatePaginationRange } from './utils';
import { colors, commonStyles, paginationStyles } from '../AgenticPaymentTransactionHistory/styles';

const PaymentHistory = () => {
  const [records, setRecords]         = useState([]);
  const [totalCount, setTotalCount]   = useState(0);
  const [totalPages, setTotalPages]   = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [countryFilter, setCountryFilter]   = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [monthFilter, setMonthFilter]       = useState('');
  const [yearFilter, setYearFilter]         = useState('');
  const [searchQuery, setSearchQuery]       = useState('');

  const fetchHistory = useCallback(async (page, country, currency, month, year, search) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: ITEMS_PER_PAGE.toString() });
      if (country)  params.set('country', country);
      if (currency) params.set('currency', currency);
      if (month)    params.set('month', month);
      if (year)     params.set('year', year);
      if (search)   params.set('search', search);
      const data = await getRequest(`${API_ENDPOINTS.PAYMENT_HISTORY}?${params.toString()}`);
      if (!data.success) throw new Error(data.error || 'Failed to load payment history');

      setRecords(data.data?.referrals || []);
      setTotalCount(data.data?.total_count || 0);
      setTotalPages(data.data?.total_pages || 1);
    } catch (err) {
      setError(err.message || 'Failed to load payment history');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(1, '', '', '', '', ''); }, [fetchHistory]);

  const applyFilters = useCallback((country, currency, month, year, search) => {
    setCurrentPage(1);
    fetchHistory(1, country, currency, month, year, search);
  }, [fetchHistory]);

  const handleCountryChange  = (val) => { setCountryFilter(val);  applyFilters(val, currencyFilter, monthFilter, yearFilter, searchQuery); };
  const handleCurrencyChange = (val) => { setCurrencyFilter(val); applyFilters(countryFilter, val, monthFilter, yearFilter, searchQuery); };
  const handleMonthChange    = (val) => { setMonthFilter(val);    applyFilters(countryFilter, currencyFilter, val, yearFilter, searchQuery); };
  const handleYearChange     = (val) => { setYearFilter(val);     applyFilters(countryFilter, currencyFilter, monthFilter, val, searchQuery); };
  const handleSearchChange   = (val) => { setSearchQuery(val);    applyFilters(countryFilter, currencyFilter, monthFilter, yearFilter, val); };
  const handlePageChange = (page) => { setCurrentPage(page); fetchHistory(page, countryFilter, currencyFilter, monthFilter, yearFilter, searchQuery); };

  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem   = Math.min(currentPage * ITEMS_PER_PAGE, totalCount);
  const pages     = calculatePaginationRange(currentPage, totalPages);

  return (
    <div>
      {/* Filters */}
      <div style={{ ...commonStyles.filterBar, gap: '10px', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search by referee name, referral ID, or email..."
          style={{ ...commonStyles.input, flex: '1', minWidth: '220px' }}
        />
        <input
          type="text"
          value={countryFilter}
          onChange={(e) => handleCountryChange(e.target.value)}
          placeholder="Country (e.g. NG)"
          style={{ ...commonStyles.input, maxWidth: '130px' }}
        />
        <input
          type="text"
          value={currencyFilter}
          onChange={(e) => handleCurrencyChange(e.target.value)}
          placeholder="Currency (e.g. NGN)"
          style={{ ...commonStyles.input, maxWidth: '140px' }}
        />
        <select
          value={monthFilter}
          onChange={(e) => handleMonthChange(e.target.value)}
          style={{ padding: '8px 12px', border: `1px solid ${colors.gray400}`, borderRadius: '4px', fontSize: '13px', minWidth: '110px' }}
        >
          <option value="">All Months</option>
          {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
            <option key={i + 1} value={i + 1}>{m}</option>
          ))}
        </select>
        <input
          type="number"
          value={yearFilter}
          onChange={(e) => handleYearChange(e.target.value)}
          placeholder="Year"
          min="2020"
          max="2099"
          style={{ ...commonStyles.input, maxWidth: '90px' }}
        />
      </div>

      <div style={{ ...commonStyles.panel, marginBottom: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table data-testid="payment-history-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['Referral ID', 'Referrer Email', 'Referee Name', 'Country', 'Currency', 'Reward Amount', 'Status', 'Created At', 'Paid At', 'Payment TX ID'].map((h) => (
                  <th key={h} style={{ padding: '14px 16px', textAlign: 'left', color: '#fff', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', background: 'linear-gradient(135deg, #2e3b61 0%, #3d4d7a 100%)', borderBottom: '2px solid #e58734' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ width: '32px', height: '32px', border: `3px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                    <p style={{ color: colors.gray600, fontSize: '14px', margin: 0 }}>Loading payment history...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: '#721c24', marginBottom: '12px' }}>{error}</p>
                    <button type="button" onClick={() => fetchHistory(currentPage)} style={{ padding: '8px 16px', background: colors.primary, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: '60px 20px', textAlign: 'center', background: colors.gray100 }}>
                    <p style={{ margin: 0, color: colors.gray600, fontSize: '14px' }}>No payment history found.</p>
                  </td>
                </tr>
              ) : (
                records.map((record, idx) => {
                  const badgeStyle = getStatusBadgeStyle(record.status);
                  const tdStyle    = { padding: '12px 16px', borderBottom: `1px solid ${colors.border}`, verticalAlign: 'middle' };
                  return (
                    <tr key={record.id} style={{ background: idx % 2 === 0 ? colors.white : colors.bgLight }}>
                      <td style={tdStyle}><code style={{ fontSize: '12px', fontFamily: 'monospace', background: colors.gray200, padding: '2px 6px', borderRadius: '3px' }}>{record.referral_ext_id || record.id}</code></td>
                      <td style={tdStyle}>{record.referrer_email || '-'}</td>
                      <td style={tdStyle}>{record.referee_name || '-'}</td>
                      <td style={tdStyle}>{record.country || '-'}</td>
                      <td style={tdStyle}>{record.reward_currency || '-'}</td>
                      <td style={tdStyle}><strong>{record.reward_amount || '-'}</strong></td>
                      <td style={tdStyle}>
                        <span style={{ ...badgeStyle, display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' }}>{record.status}</span>
                      </td>
                      <td style={tdStyle}>{formatReferralDate(record.created_at)}</td>
                      <td style={tdStyle} data-testid="paid-at-cell">{formatReferralDate(record.paid_at)}</td>
                      <td style={tdStyle} data-testid="payment-tx-id-cell">
                        {record.payment_tx_id
                          ? <code style={{ fontSize: '12px', fontFamily: 'monospace', background: colors.gray200, padding: '2px 6px', borderRadius: '3px' }}>{record.payment_tx_id}</code>
                          : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div style={paginationStyles.container}>
            <span style={paginationStyles.info}>
              Showing {startItem}, {endItem} of {totalCount} records
            </span>
            <div style={paginationStyles.nav}>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ ...paginationStyles.navButton, background: currentPage === 1 ? colors.gray300 : colors.primary, color: currentPage === 1 ? colors.gray500 : colors.white, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                ← Prev
              </button>
              {pages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => handlePageChange(page)}
                  style={{ ...paginationStyles.pageButton, background: page === currentPage ? colors.accent : colors.white, color: page === currentPage ? colors.white : colors.gray700, border: page === currentPage ? 'none' : `1px solid ${colors.border}`, fontWeight: page === currentPage ? 700 : 400 }}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ ...paginationStyles.navButton, background: currentPage === totalPages ? colors.gray300 : colors.primary, color: currentPage === totalPages ? colors.gray500 : colors.white, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
